import Fuse from 'fuse.js'
import i18next from 'i18next'
import lodash from 'lodash'
import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { SearchBox } from '../ui/SearchBox'
import { createPromiseManager } from '../util/PromiseManager'
import { initialPromiseState, type PromiseState } from '../util/PromiseState'
import { createStateLens } from '../util/react'
import { GuideListItem } from './GuideListItem'
import type { AppGuide, ParentInfo } from './Model'

interface Props {
    deps: CommonDeps
    fetchAppGuides: () => Promise<AppGuide[]>
    onClickGuideParentInfo?: (parentInfo: ParentInfo) => void
    onGuideClick: (guide: AppGuide) => void
    renderCachedImage: RenderCachedImage
    withSearch?: boolean // If not added search bar will not be displayed
    children?: (guideId: AppGuide['id']) => React.ReactNode
}

interface State {
    allGuidesState: PromiseState<AppGuide[]>
    guides: AppGuide[]
    fuse: Fuse<AppGuide> | null
    searchTerm: string
}

export class GuideList extends React.Component<Props, State> {
    private promiseManagers = {
        allGuides: createPromiseManager(() => this.props.deps, createStateLens(this, 'allGuidesState')),
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        allGuidesState: initialPromiseState,
        guides: [],
        fuse: null,
        searchTerm: '',
    }

    public async componentDidMount() {
        const allGuides = await this.promiseManagers.allGuides.trigger(this.props.fetchAppGuides)
        const fuse = this.props.withSearch ? new Fuse(allGuides, { keys: ['name'], threshold: 0.3 }) : null

        this.setState({ guides: allGuides, fuse })
    }

    private tryRender = () => {
        return (
            <div className="AppCore__guide__GuideList">
                {this.promiseManagers.allGuides.render((_) => this.renderGuides(this.state.guides))}
            </div>
        )
    }

    private renderGuides(guides: AppGuide[]) {
        return (
            <>
                {this.props.withSearch && (
                    <div className="AppCore__guide__GuideList-search-box">
                        <SearchBox
                            deps={this.props.deps}
                            size="large"
                            placeholder={i18next.t('appcore:common.search')}
                            searchTerm={this.state.searchTerm}
                            onChange={(searchTerm: string) =>
                                this.setState({ searchTerm }, () => this.onSearchTermChange())
                            }
                        />
                    </div>
                )}
                {this.props?.withSearch && this.state.searchTerm !== '' && guides.length === 0
                    ? this.renderNoSearchResultMessage()
                    : this.renderGuideListItems(guides)}
            </>
        )
    }

    private onSearchTermChange = lodash.debounce(() => {
        // If no search term, show all the guides
        const filteredGuides: AppGuide[] =
            this.state.searchTerm.trim().length > 0
                ? this.state.fuse.search(this.state.searchTerm).map((result) => result.item)
                : this.state.allGuidesState.status === 'resolved'
                  ? this.promiseManagers.allGuides.maybeGet()
                  : []
        this.setState({ guides: filteredGuides })
    }, 250)

    private renderNoSearchResultMessage = () => {
        return (
            <div className="AppCore__guide__GuideList-search-no-results">
                {i18next.t('appcore:common.noSearchResultsText')}
            </div>
        )
    }

    private renderGuideListItems = (guides: AppGuide[]) => {
        return (
            <div>
                {guides.map((guide) => (
                    <GuideListItem
                        deps={this.props.deps}
                        guide={guide}
                        iconUrl={guide.iconUrl}
                        key={guide.id}
                        onClick={() => this.props.onGuideClick(guide)}
                        renderCachedImage={this.props.renderCachedImage}
                        onClickGuideParentInfo={this.props.onClickGuideParentInfo}
                    >
                        {this.props.children && this.props.children(guide.id)}
                    </GuideListItem>
                ))}
            </div>
        )
    }
}
